/**
 * Client-side receipt compression constants.
 *
 * These live in shared/ rather than in the web app so that raising them later
 * is one edit, and so the API can record what the client was supposed to do.
 *
 * Why 2600/0.85 rather than something smaller: a phone photo is 3-12MB and
 * uploading that over hotel wifi is the single worst part of the experience,
 * but over-compressing destroys information that OCR will want later and
 * cannot recover -- you cannot upscale detail discarded at capture. 2600px at
 * q0.85 lands around 500-700KB, roughly 6x smaller than the original, while
 * keeping printed totals comfortably legible.
 */
export const MAX_EDGE = 2600;
export const QUALITY = 0.85;
export const THUMB_EDGE = 400;
export const THUMB_QUALITY = 0.7;

/**
 * Tall thermal receipts are the case a fixed long-edge cap damages most: a
 * 1000x4000 receipt capped to 2600 on the long edge leaves only 650px across
 * the text. Above this aspect ratio we hold the SHORT edge instead.
 */
export const TALL_ASPECT_THRESHOLD = 2.5;
export const MIN_SHORT_EDGE = 1200;

/** Hard ceiling accepted by the upload-init endpoint, post-compression. */
export const MAX_UPLOAD_BYTES = 12 * 1024 * 1024;

export interface TargetSize {
  width: number;
  height: number;
}

/**
 * Compute the target dimensions for a source image. Pure, so the API can
 * assert the client honoured it and tests can cover the tall-receipt branch
 * without a canvas.
 */
export function targetSizeFor(width: number, height: number): TargetSize {
  if (width <= 0 || height <= 0) throw new Error('Image dimensions must be positive');

  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  const aspect = longEdge / shortEdge;

  let scale: number;
  if (aspect > TALL_ASPECT_THRESHOLD) {
    // Hold the short edge, but never upscale and never exceed a sane long edge.
    scale = Math.min(1, MIN_SHORT_EDGE / shortEdge);
  } else {
    scale = Math.min(1, MAX_EDGE / longEdge);
  }

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * EXIF orientation values that represent a 90-degree rotation, where the
 * decoded bitmap's width and height swap. Used to detect whether the browser
 * already applied the rotation for us -- see the web app's exif.ts for why
 * that detection is necessary and what goes wrong without it.
 */
export const QUARTER_TURN_ORIENTATIONS: readonly number[] = [5, 6, 7, 8];

export function isQuarterTurn(orientation: number | null | undefined): boolean {
  return orientation != null && QUARTER_TURN_ORIENTATIONS.includes(orientation);
}
